import Button from '@/components/UI/Button/Button';
import Icon from '@/components/UI/Icon/Icon';
import { useState, type FC } from 'react';
import styles from './RefreshButton.module.scss';

interface RefreshButtonProps {
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  testIdPrefix?: string;
}

/**
 * Icon-only control that triggers an APITable refetch. Visible on every table by default;
 * sits beside the ViewSelector (or alone in that slot when the selector is hidden) so users
 * can poll for new data without reloading the page — useful for tables backed by async work
 * like the Exports list.
 *
 * On click, the icon spins one full revolution. The spin is decoupled from the actual fetch
 * so the user always gets visual feedback even when the response comes back instantly from
 * cache; `animationend` clears the state so a follow-up click re-triggers it cleanly.
 */
const RefreshButton: FC<RefreshButtonProps> = ({ onClick, disabled = false, testIdPrefix }) => {
  const [spinning, setSpinning] = useState(false);

  const handleClick = () => {
    if (disabled) {
      return;
    }
    setSpinning(true);
    void onClick();
  };

  const testId = testIdPrefix !== undefined && testIdPrefix !== '' ? `${testIdPrefix}__refresh` : 'apitable__refresh';

  return (
    <Button
      onClick={handleClick}
      disabled={disabled}
      plain
      className={styles.refresh}
      tooltip="Refresh"
      testId={testId}
    >
      <span
        className={spinning ? styles.spinning : 'inline-flex items-center'}
        onAnimationEnd={() => setSpinning(false)}
      >
        <Icon icon="refresh" className="dark-gray" width={22} height={22} viewBox="0 0 24 24" />
      </span>
    </Button>
  );
};

export default RefreshButton;
